/**
 * Screen Shader | Smart Screen Tinting
 *
 * Tints the page by default, so its content script acts on every page with no user
 * action. Exposes no reachable web-accessible resource, but leaves two vendor-named
 * marks, both found with `tooling/probe-interactions.js`:
 *
 *   - a `<screen-shader>` custom element it appends for the tint overlay, and
 *   - a `ss-shaded-scrollbars` class it adds to `<html>`.
 *
 * Both tokens are product-specific, so they identify this extension rather than "some
 * dark-mode tool". Behavioural, so they only fire while the extension is actively
 * shading — which is its default state.
 *
 * Observed on version 2.1.1.
 */

import { probeSelector } from '../core/probe';
import type { ExtensionDefinition } from '../core/types';

const ID = 'fmlboobidmkelggdainpknloccojpppi';

export const screenShader: ExtensionDefinition = {
  id: ID,
  name: 'Screen Shader | Smart Screen Tinting',
  storeUrl: `https://chromewebstore.google.com/detail/${ID}`,
  users: 100_000,
  interactive: true,
  notes:
    'Detected behaviourally: appends a <screen-shader> custom element and adds ' +
    'html.ss-shaded-scrollbars while shading the page (its default). No reachable ' +
    'web-accessible resource, so these are the signals.',
  signals: [
    {
      id: 'dom-screen-shader-element',
      kind: 'dom',
      description:
        'Look for the <screen-shader> custom element the extension appends for its tint ' +
        'overlay — a product-specific tag name.',
      weight: 1,
      run: () => probeSelector('screen-shader', { timeoutMs: 2500 }),
    },
    {
      id: 'dom-screen-shader-scrollbars',
      kind: 'dom',
      description:
        'Look for html.ss-shaded-scrollbars — the vendor-namespaced class the extension ' +
        'adds to the document element, corroborating the identification.',
      weight: 0.5,
      run: () => probeSelector('html.ss-shaded-scrollbars', { timeoutMs: 2500 }),
    },
  ],
};
